'use client';
//TODO: Complete
import { iSizeConfigurations } from '@/app/models/configurations';
import styles from './storagesizesadvanced.module.scss';
import { logError } from '@/app/helpers/azureBlobLogger';
import { iStorageSizeTracking } from '@/app/models/storageSizeTracking';
import { iStorageSizeAdvanced } from '@/app/models/storageSizeAdvanced';
import StorageSizeCardAdvanced from '../StorageSizeCardAdvanced/StorageSizeCardAdvanced';
import { useEffect, useRef, useState } from 'react';
import { clientLogInfo } from '@/app/helpers/clientLoggingHelper';
import { iSizeGroup } from '@/app/models/sizeGroup';
import { iGetSiteByIdRequestModel } from '@/app/models/apiModels/requestModels/getSiteByIdRequestModel';
import StorageUnitsSlider from './StorageUnitsSlider';
import Loader from '../Loader/Loader';
import Accordion from '../Accordion/Accordion';
import { GenerateAllUnitCardsFromGroup, GenerateCardsFromSizeGroup, GenerateCardsFromUnitSize, GenerateUnitCardsFromUnits, PopulateGroupSizeRange } from '@/app/helpers/storageSizeAdvancedHelper';
import { SCREEN_BREAKPOINTS } from '@/app/helpers/constants';
import { doUseOrderStorageStore } from '@/stores/order-storage-store';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from 'zustand';
import { GenerateUUID, TranslateWithReplace } from '@/app/helpers/clientUtilityHelper';

interface Props {
    title?: string;
    preamble?: string;
    lastItem: iLastSizesItem;
    country: string;
    facilityId?: string | string[];
    facilityCity?: string;
    facilityName?: string;
    translations: any;
    sizeConfigurations: iSizeConfigurations;
    chooseFacilityUrl: string;
    orderStep1Url: string;
    temporaryOrgName?: string;
    useCustomStyling?: boolean;
    minInsurancePrice: number;
}

interface iLastSizesItem {
    title: string;
    text: string;
    buttonLink: string;
    buttonText: string;
}

interface iSizeUnitCards {
    units: iStorageSizeAdvanced[];
    groupNameShort: string;
    groupName: string;
    sizeRange: string;
    isOpen: boolean;
}

export default function StorageSizesAdvanced({ title, preamble, lastItem, country, facilityId, facilityCity, facilityName, translations, sizeConfigurations, chooseFacilityUrl, orderStep1Url, temporaryOrgName, useCustomStyling, minInsurancePrice }: Props) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [groupSizes, setGroupSizes] = useState<iSizeGroup[]>([]);
    const [activeGroupSize, setActiveGroupSize] = useState<iSizeGroup | undefined>(undefined);
    const [activeItems, setActiveItems] = useState<iStorageSizeAdvanced[]>([]);
    const [facilityTracking, setFacilityTracking] = useState<iStorageSizeTracking | undefined>(undefined);
    const [facilityTrackingIsSet, setFacilityTrackingIsSet] = useState<boolean>(false);
    const [unitCardsBySize, setUnitCardsBySize] = useState<iSizeUnitCards[]>([]);
    const [unitCardsBySizeDefault, setUnitCardsBySizeDefault] = useState<iSizeUnitCards[]>([]);
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const accordionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isOnStartPage, setIsOnStartPage] = useState<boolean>(!facilityId || facilityId === undefined || (Array.isArray(facilityId) && facilityId.length === 0));
    const [isOnAreaPage, setIsOnAreaPage] = useState<boolean>(!!facilityId && Array.isArray(facilityId) && facilityId.length > 0);

    const trackingStore = doUseOrderStorageStore(useShallow((state) => state.tracking));
    const setTrackingValue = useStore(
        doUseOrderStorageStore,
        useShallow((state) => state)
    )?.setTrackingValue;

    useEffect(() => {
        const fetchSizeGroups = async () => {
            try {
                const requestModel: iGetSizeGroupsRequestModel = {
                    country: country,
                    facilityId: facilityId
                };

                const response = await fetch('/api/size/getSizeGroups', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestModel),
                });

                if (!response.ok) {
                    setIsLoading(false);
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();

                let sizeGroups = data as iSizeGroup[];

                const allSizeGroup: iSizeGroup = {
                    name: translations.general.all,
                    shortName: translations.general.all,
                    hasGeneratedCards: true,
                    isAll: true,
                    unitSizes: [],
                    cards: []
                };

                const allSizeGroupCards = GenerateCardsFromSizeGroup(sizeGroups, orderStep1Url, chooseFacilityUrl, sizeConfigurations, translations, isOnStartPage);
                if (typeof facilityId === "string") {
                    const allCards: iStorageSizeAdvanced[] = [];
                    sizeGroups.forEach(group => {
                        const groupCards = GenerateCardsFromUnitSize(group.unitSizes, group.shortName, orderStep1Url, chooseFacilityUrl, sizeConfigurations, translations, isOnStartPage);
                        allCards.push(...groupCards);
                    });
                    allSizeGroup.cards = allCards;
                } else {
                    allSizeGroup.cards = allSizeGroupCards;
                }

                allSizeGroup.hasGeneratedCards = true;
                sizeGroups.push(allSizeGroup);
                clientLogInfo("StorageSizesAdvanced.tsx", "fetchSizeGroups", `Fetched items: ${sizeGroups.length} items`);
                setGroupSizes(sizeGroups);

                if (sizeGroups && sizeGroups.length > 0) {
                    sizeGroups.forEach(group => {
                        PopulateGroupSizeRange(group);
                    });
                }

                const unitCardsBySizeTemp: iSizeUnitCards[] = [];

                sizeGroups.forEach(group => {
                    if (group.isAll) {
                        return;
                    }

                    const unitCards = GenerateAllUnitCardsFromGroup(group, sizeConfigurations, orderStep1Url, translations);
                    unitCardsBySizeTemp.push({
                        units: [...unitCards],
                        groupNameShort: group.shortName,
                        groupName: group.name,
                        sizeRange: group.sizeRange ?? "",
                        isOpen: false
                    });
                });

                setUnitCardsBySize(unitCardsBySizeTemp);
                // Create a deep copy for unitCardsBySizeDefault
                setUnitCardsBySizeDefault(JSON.parse(JSON.stringify(unitCardsBySizeTemp)));

                setActiveGroupSize(sizeGroups[sizeGroups.length - 1]);
                if (sizeGroups && unitCardsBySizeTemp) {
                    const bookingId = trackingStore?.bookingId ?? GenerateUUID();
                    setTrackingValue("bookingId", bookingId);
                }
            } catch (error) {
                logError("Error in StorageSizesAdvanced", 'Error when fetching StorageSizesAdvanced', `country: ${country}, facilityId: ${facilityId}`, 'Error', error);
            } finally {
                setIsLoading(false);
            }
        }


        const getFacilityTracking = async () => {
            const singleFacilityId = typeof facilityId === "string" ? facilityId : undefined;

            if (!singleFacilityId) {
                setFacilityTrackingIsSet(true);
                return;
            }

            const requestModel: iGetSiteByIdRequestModel = {
                country: country,
                facilityId: singleFacilityId,
                showOffline: true
            };

            const response = await fetch('/api/site/getSiteById', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestModel),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            const city = facilityCity || data?.town || "";
            const name = facilityName || data?.name || "";

            setFacilityTracking({
                facilityId: data.id,
                facilityCode: data.code,
                facilityName: `${city.trim().replace(/\s/g, "_")}-${name.trim().replace(/\s/g, "_")}`,
            });
        }

        fetchSizeGroups();

        if (facilityId && facilityId !== undefined) {
            getFacilityTracking();
        }
    }, []);

    useEffect(() => {
        const updateScreenSize = () => {
            setIsDesktop(window.innerWidth > SCREEN_BREAKPOINTS.DESKTOP);
        }

        updateScreenSize();
        window.addEventListener("resize", updateScreenSize);
        return () => window.removeEventListener("resize", updateScreenSize);
    }, [])

    useEffect(() => {
        setFacilityTrackingIsSet(true);
    }, [facilityTracking]);

    useEffect(() => {
        if (!activeGroupSize) {
            return;
        }

        if (activeGroupSize && !activeGroupSize.hasGeneratedCards) {
            const activeGroupSizeTemp = { ...activeGroupSize };
            activeGroupSizeTemp.cards = GenerateCardsFromUnitSize(activeGroupSize.unitSizes, activeGroupSize.shortName, orderStep1Url, chooseFacilityUrl, sizeConfigurations, translations, isOnStartPage);
            activeGroupSizeTemp.hasGeneratedCards = true;
            setActiveGroupSize(activeGroupSizeTemp);
            return;
        }

        if (activeGroupSize && activeGroupSize.cards && activeGroupSize.cards.length > 0) {
            setActiveItems(activeGroupSize.cards);
            return;
        }

        setActiveItems([]);
    }, [activeGroupSize]);

    useEffect(() => {
        const openGroupIndex = unitCardsBySize.findIndex((group) => group.isOpen); // Find the index of the open group

        if (openGroupIndex !== -1 && accordionRefs.current[openGroupIndex]) {
            const element = accordionRefs.current[openGroupIndex];
            const offset = 150; // Stop scrolling 150px before the top
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const scrollToPosition = elementPosition - offset;

            // Custom smooth scrolling
            const scrollDuration = 1000; // Duration in milliseconds
            const start = window.scrollY;
            const distance = scrollToPosition - start;
            let startTime: number | null = null;

            const scrollStep = (timestamp: number) => {
                if (!startTime) startTime = timestamp;
                const progress = timestamp - startTime;
                const percent = Math.min(progress / scrollDuration, 1); // Ensure it doesn't exceed 1
                const easeInOutQuad = percent < 0.5
                    ? 2 * percent * percent
                    : -1 + (4 - 2 * percent) * percent; // Ease-in-out function
                window.scrollTo(0, start + distance * easeInOutQuad);

                if (progress < scrollDuration) {
                    requestAnimationFrame(scrollStep); // Continue scrolling
                }
            };

            requestAnimationFrame(scrollStep);
        }
    }, [unitCardsBySize]);

    const activeButtonClass = function (group: iSizeGroup) {
        const isActive = group.shortName === activeGroupSize?.shortName;

        let classes = `${styles.filterButton} flex-grow ${isActive ? `${styles.isActive} bg-gs-green` : ""}`// "btn flex-grow";

        return classes;
    }

    const getUnitCardsFromUnitSize = function (unitSizeIdentification: string): iStorageSizeAdvanced[] {
        let storageSizes: iStorageSizeAdvanced[] = [];
        const splitIdentification = unitSizeIdentification.split("|");

        if (splitIdentification.length === 0) {
            return storageSizes;
        }

        const currentShortName = splitIdentification[0];
        const currentGroupSize = groupSizes.find(group => group.shortName === currentShortName);

        if (!currentGroupSize) {
            return storageSizes;
        }

        if (splitIdentification.length === 1) {
            currentGroupSize.unitSizes.forEach(unitSize => {
                const unitSizeUnitCards = GenerateUnitCardsFromUnits(unitSize.units, currentGroupSize.shortName, sizeConfigurations, orderStep1Url, translations);
                storageSizes.push(...unitSizeUnitCards);
            });
        } else {
            const size = splitIdentification[1];
            const currentUnitSize = currentGroupSize.unitSizes.find(unitSize => unitSize.info?.physicalSize?.toString() === size);

            if (!currentUnitSize) {
                return storageSizes;
            }

            storageSizes = GenerateUnitCardsFromUnits(currentUnitSize.units, currentGroupSize.shortName, sizeConfigurations, orderStep1Url, translations);
        }

        return storageSizes;
    }


    const handleCardClick = (card: iStorageSizeAdvanced) => {
        window.dataLayer = window.dataLayer || [];
        let pageType: string = "non-area-or-facility-page";

        if (facilityId && typeof facilityId === "string") {
            pageType = "facility-page";
        } else if (facilityId && Array.isArray(facilityId)) {
            pageType = "area-page";
        }

        let trackingObject: any = {
            event: "booking_step",
            step: `select_card_on_${pageType}`,
            size: card.size,
            sizeGroup: card.shortName,
            advanced_block: true,
            card_id: card.id,
            card_identifier: card.identifier
        }

        if (card.unitId) {
            trackingObject.unit_id = card.unitId;
        }

        if (facilityId && typeof facilityId === "string") {
            trackingObject.site_code = facilityId ?? "";
            trackingObject.site_name = facilityName ?? "";
        } else if (facilityId && Array.isArray(facilityId)) {
            trackingObject.site_code = facilityId.join(",") ?? "";
        }

        window.dataLayer.push(trackingObject);
        
        if (card.isLastOne || isOnStartPage) {
            return;
        }

        const unitCards = getUnitCardsFromUnitSize(card.identifier);
        let unitCardsBySizeTemp = unitCardsBySize;

        unitCardsBySizeTemp.forEach((unitCard) => {
            unitCard.isOpen = false;
        });

        let currentSize = unitCardsBySizeTemp.find(unitCards => unitCards.groupNameShort === card.shortName);

        if (!currentSize) {
            return;
        }

        currentSize.units = unitCards;
        currentSize.sizeRange = card.size;
        currentSize.isOpen = true;

        setUnitCardsBySize([...unitCardsBySize]);
    }

    const handleChangeSizeGroupClick = (group: iSizeGroup) => {
        setActiveGroupSize(group);
        setUnitCardsBySize(JSON.parse(JSON.stringify(unitCardsBySizeDefault)));
        setTrackingValue("sizeGroup", group.name);
        setTrackingValue("size", group.sizeRange);
    }

    return (
        <div className="min-h-[100px]">
            {isLoading ? (
                <Loader
                    loading={isLoading}
                    useFullWindow={false}
                    useNoBackground={true}
                />
            ) : (
                <div>
                    {title && (
                        <h2>{title}</h2>
                    )}
                    <div className="flex gap-[0.25rem] textSmall">
                        <span className="font-semibold">*{translations.pages.campaignListingPage.isAdded} </span>
                        <span>{TranslateWithReplace(translations.pages.campaignListingPage.insuranceText, { sum: minInsurancePrice.toString() })}</span>
                    </div>
                    <div>
                        <div className={`${styles.sizeGroupsContainerDesktop} hidden lg:flex flex-wrap w-full justify-start mb-4`}>
                            {groupSizes && groupSizes.length > 0 ? groupSizes.map((group, index) => (
                                <button
                                    key={index}
                                    className={`${activeButtonClass(group)}`}
                                    onClick={() => handleChangeSizeGroupClick(group)}
                                >
                                    {group.name}
                                </button>
                            )) : <span>{translations.blocks.storageSizesAdvanced.noSizesFound}</span>}
                        </div>
                        <div className={`${styles.sizeGroupsContainerSmall} lg:hidden grid grid-cols-1 w-full mb-4`}>
                            {groupSizes && groupSizes.length > 0 ? (
                                <>
                                    {groupSizes.reduce<JSX.Element[][]>((rows, group, index) => {
                                        const rowIndex = Math.floor(index / 4); // Determine the row index

                                        if (!rows[rowIndex]) rows[rowIndex] = []; // Initialize the row if it doesn't exist

                                        rows[rowIndex].push(
                                            <button
                                                key={index}
                                                className={`${activeButtonClass(group)}`}
                                                onClick={() => handleChangeSizeGroupClick(group)}
                                            >
                                                {group.shortName}
                                            </button>
                                        );
                                        return rows;
                                    }, []).map((row, rowIndex) => (
                                        <div key={rowIndex} className="row-container flex justify-start">
                                            {row}
                                        </div>
                                    ))
                                    }
                                </>
                            ) : (
                                <span>{translations.blocks.storageSizesAdvanced.noSizesFound}</span>
                            )}
                        </div >
                    </div >
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                        {activeItems && facilityTrackingIsSet && activeItems.map((storage, index) => (
                            <StorageSizeCardAdvanced
                                key={index}
                                storage={storage}
                                translations={translations}
                                temporaryOrgName={temporaryOrgName}
                                onClick={handleCardClick}
                                country={country}
                                useCustomStyling={useCustomStyling}
                                isOnStartPage={isOnStartPage}
                                isOnAreaPage={isOnAreaPage}
                            />
                        ))}
                        <div
                            className="no-underline text-center block rounded-3xl p-4 bg-[#D2F1F3] content-center"
                            key="info">
                            <h3 className="text-lg font-bold">{lastItem.title}</h3>
                            <p className="mb-6 text-small lg:text-base">
                                {lastItem.text}
                            </p>
                            <a href={lastItem.buttonLink} title={lastItem.buttonText} className='btn dark-contrast'>
                                {lastItem.buttonText ? (
                                    <>
                                        {lastItem.buttonText}
                                    </>
                                ) : (
                                    <>
                                        {translations.general.moreInformation}
                                    </>
                                )}
                            </a>
                        </div>
                    </div>
                    {(!isOnStartPage && unitCardsBySize) && (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-6">
                            {unitCardsBySize
                                .map((group, index) => (
                                    <div
                                        key={index}
                                        ref={(el) => {
                                            accordionRefs.current[index] = el; // Assign the element to the ref array
                                        }}
                                    >
                                        <Accordion
                                            title={`${group.sizeRange} m² - ${group.groupName}`}
                                            key={index}
                                            translations={translations}
                                            className={`border border-gs-black rounded-[16px]`}
                                            expand={group.isOpen}
                                            onExpandCallback={(isExpanded) => {
                                                const unitCardsBySizeTemp = unitCardsBySize;
                                                unitCardsBySizeTemp[index].isOpen = isExpanded;
                                                setUnitCardsBySize([...unitCardsBySizeTemp]);
                                            }}
                                        >

                                            <StorageUnitsSlider
                                                key={index}
                                                handleCardClick={handleCardClick}
                                                units={group.units}
                                                translations={translations}
                                                temporaryOrgName={temporaryOrgName}
                                                visibleCardCount={isDesktop || window.innerWidth <= 768 && window.innerWidth > 445 ? 2 : 1}
                                                country={country}
                                                isExpanded={group.isOpen}
                                                isOnAreaPage={isOnAreaPage}
                                            />
                                        </Accordion>
                                    </div>
                                ))}
                        </div>
                    )}
                </div >
            )}
        </div>
    )
}


